import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { concatMap, takeUntil, toArray } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { SubsectionService } from '../../services/subsection.service';
import { GridOption, OptionVersion } from '../../interfaces';
import { OverlayService } from '../../services/overlayService';
import { ReleaseTreeService } from '../../../project-subsection/services/release-tree-service';
import { ReleaseAssetSimple } from '../../../project-subsection/models/release';
import { SubsectionTreeService } from '../../../project-subsection/services/subsection-tree.service';

export type SubsectionVersionListResult = {
  type: 'reorder' | 'edit' | 'success';
  orderedIds?: number[];
  deletedIds?: number[];
  version?: OptionVersion;
  newCurrentVersion?: number;
};

@Component({
  selector: 'mtm-version-list',
  templateUrl: './subsection-version-list.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrl: './subsection-version-list.component.scss',
  standalone: false,
})
export class SubsectionVersionListComponent implements OnInit, OnDestroy {
  private readonly deleteList: number[] = [];
  private _versions: OptionVersion[] = [];

  @Input() projectId: string;
  @Input() sectionId: string;
  @Input() subsectionId: string;
  @Input() itemId: string;
  @Input() option?: GridOption;
  @Input() isPreviewOnly: boolean = false;

  get versions(): OptionVersion[] {
    return this._versions;
  }

  @Input()
  set versions(values: OptionVersion[]) {
    this._versions = values ?? [];
    this._versions.forEach((v: any) => {
      v.items = v.files;
    });
  }

  isLoading: boolean = false;
  private readonly ngUnsubscribe = new Subject();

  // Properties for locking mechanism
  private readonly releaseTreeService = inject(ReleaseTreeService);
  private readonly subsectionTreeService = inject(SubsectionTreeService);
  releaseRecord: Record<string, ReleaseAssetSimple> = {};

  constructor(
    private readonly apiService: ApiService,
    private readonly subsectionService: SubsectionService,
    private readonly notificationService: NotificationService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe,
    private readonly activeModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state) => {
          this.releaseRecord = state.releaseRecord || {};
        },
      });
  }

  onDrop(event: CdkDragDrop<OptionVersion[]>): void {
    moveItemInArray(this._versions, event.previousIndex, event.currentIndex);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  // Methods to check if version is locked
  isVersionLocked(versionNumber: number): boolean {
    if (!this.itemId) return false;
    const versionId = `${this.itemId}-${versionNumber}`;
    return !!this.releaseRecord[versionId];
  }

  editVersion(version: OptionVersion) {
    // Don't allow editing if version is locked
    if (this.isVersionLocked(version.versionNumber)) {
      this.notificationService.open({
        title: this.translatePipe.transform('error'),
        description: this.translatePipe.transform('version_locked_cannot_edit'),
        confirmBtn: this.translatePipe.transform('accept'),
      });
      return;
    }

    this.activeModal.close({
      type: 'edit',
      version,
    });
  }

  deleteVersion(version: OptionVersion) {
    // Don't allow deletion if version is locked
    if (this.isVersionLocked(version.versionNumber)) {
      this.notificationService.open({
        title: this.translatePipe.transform('error'),
        description: this.translatePipe.transform('version_locked_cannot_delete'),
        confirmBtn: this.translatePipe.transform('accept'),
      });
      return;
    }

    const oldIndex = this._versions.findIndex((v) => v.versionNumber == version.versionNumber);
    if (oldIndex <= -1) {
      return;
    }
    if (this._versions.length <= 1) {
      this.notificationService.open({
        title: this.translatePipe.transform('versioning_edit_deleteVersionError'),
        description: this.translatePipe.transform('versioning_edit_oneVersionRequired'),
        confirmBtn: this.translatePipe.transform('accept'),
      });
      return;
    }

    this.deleteList.push(version.versionNumber);
    this._versions = this._versions.filter((v) => v.versionNumber !== version.versionNumber);
  }

  cancel() {
    this.activeModal.dismiss();
  }

  save() {
    const orderedIds = this._versions.map((version) => version.versionNumber);

    if (this.isPreviewOnly) {
      this.activeModal.close({
        type: 'reorder',
        orderedIds,
        deletedIds: this.deleteList.length > 0 ? [...this.deleteList] : undefined,
      });
      return;
    }

    this.isLoading = true;
    const $obsList: Array<Observable<any>> = [];

    if (this.deleteList.length > 0) {
      $obsList.push(
        this.subsectionService.deleteVersions(
          this.projectId,
          this.sectionId,
          this.subsectionId,
          this.itemId,
          this.deleteList
        )
      );
    }

    if (orderedIds.length > 0) {
      $obsList.push(
        this.subsectionService.reorderVersions(
          this.projectId,
          this.sectionId,
          this.subsectionId,
          this.itemId,
          orderedIds
        )
      );
    }

    if ($obsList.length > 0) {
      of(...$obsList)
        .pipe(
          concatMap((obs: Observable<any>) => obs),
          toArray()
        )
        .subscribe({
          next: () => {
            if (orderedIds.length === 0) {
              this.isLoading = false;
              this.activeModal.close({ type: 'success', orderedIds: [] });
              return;
            }
            const newActiveVersion = this.resolveNewActiveVersion(orderedIds);
            this.switchVersion(orderedIds, newActiveVersion);
          },
          error: () => {
            this.isLoading = false;
          },
        });
    } else {
      this.activeModal.close({
        type: 'success',
        orderedIds,
      });
    }
  }

  private resolveNewActiveVersion(orderedIds: number[]): number {
    const currentWasDeleted = this.deleteList.includes(this.option?.currentVersion ?? -1);
    if (currentWasDeleted && orderedIds.length > 0) {
      return Math.max(...orderedIds);
    }
    return orderedIds[0];
  }

  private switchVersion(orderedIds: number[], newActiveVersion: number): void {
    const query$: Array<Observable<any>> = [];
    query$.push(this.subsectionService.getOption(this.projectId, this.sectionId, this.subsectionId, this.itemId));

    if (!this.option.versionCache) {
      this.option.versionCache = {};
    }

    if (!this.option.versionCache[newActiveVersion]) {
      query$.push(
        this.subsectionService.getVersion({
          projectId: this.projectId,
          sectionId: this.sectionId,
          subsectionId: this.subsectionId,
          itemId: this.itemId,
          versionNumber: newActiveVersion,
        })
      );
    } else {
      this.option.files = this.option.versionCache[newActiveVersion].files;
      this.option.items = this.option.versionCache[newActiveVersion].files;
      this.option.currentVersion = newActiveVersion;
    }

    forkJoin(query$)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: any[]) => {
          const newOption = this.subsectionService.convertOptionToViewModel(result[0]);
          if (newOption.simpleVersions) {
            newOption.simpleVersions.sort((v1, v2) => v1.order - v2.order);
          }
          this.option.simpleVersions = newOption.simpleVersions;

          if (this.itemId && orderedIds.length > 0) {
            this.subsectionTreeService.reorderVersions(this.itemId, orderedIds);
          }

          if (result.length > 1) {
            const version = result[1];

            this.option.versionCache[newActiveVersion] = version;
            this.option.files = version.files;
            this.option.items = version.files;
            this.option.currentVersion = newActiveVersion;
          }

          this.updateCurrentVersionInUrl(newActiveVersion);
          this.isLoading = false;
          this.activeModal.close({
            type: 'success',
            orderedIds,
            newCurrentVersion: newActiveVersion,
          });
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private updateCurrentVersionInUrl(versionNumber: number): void {
    this.subsectionService.navigateSubsection({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subsectionId: this.subsectionId,
      ...(this.option?.topicId && { topicId: this.option.topicId }),
      ...(this.option?.optionId && { optionId: this.option.optionId }),
      versionNumber,
    });
  }
}
